import type { Element, GroupElement } from '@/components/canvas/document/publicTypes';

/** 深拷贝元素数据，避免复制/粘贴后共享 data（如 text.document）引用。 */
export function cloneElementDeep<T extends Element>(element: T): T {
  return JSON.parse(JSON.stringify(element)) as T;
}

/**
 * 将选中 id 扩展为完整子树（编组含嵌套组时递归纳入全部子孙元素）。
 * 复制编组时若只复制 group 节点而不含子元素，粘贴后多个组会共用同一批子 id。
 */
export function collectSubtreeElementIds(
  elements: Element[],
  rootIds: Iterable<string>,
): Set<string> {
  const ids = new Set(rootIds);
  const byId = new Map(elements.map((el) => [el.id, el]));

  const visit = (id: string) => {
    const el = byId.get(id);
    if (!el || el.type !== 'group') return;
    for (const childId of (el as GroupElement).data.children ?? []) {
      if (!ids.has(childId)) {
        ids.add(childId);
        visit(childId);
      }
    }
  };

  for (const id of ids) {
    visit(id);
  }
  return ids;
}

/** 复制剪贴板用：选中元素 + 编组子孙，并深拷贝。 */
export function cloneElementsForClipboard(
  elements: Element[],
  selectedIds: Iterable<string>,
): Element[] {
  const ids = collectSubtreeElementIds(elements, selectedIds);
  return elements.filter((el) => ids.has(el.id)).map((el) => cloneElementDeep(el));
}

/** 粘贴后仅选中子树顶层（编组本身），不选中组内子元素。 */
export function pasteRootElementIds(elements: Element[]): string[] {
  const ids = new Set(elements.map((el) => el.id));
  return elements
    .filter((el) => !el.layout.parentId || !ids.has(el.layout.parentId))
    .map((el) => el.id);
}

/** 粘贴子树中的顶层节点（编组本体），用于计算粘贴位移，避免组内相对坐标被加偏移。 */
export function pasteSubtreeRoots(elements: Element[]): Element[] {
  const ids = new Set(elements.map((el) => el.id));
  return elements.filter((el) => !el.layout.parentId || !ids.has(el.layout.parentId));
}

/** 组内子元素保持相对坐标；仅顶层节点随粘贴目标平移。 */
export function shouldApplyPasteTranslation(
  element: Element,
  pastedSourceIds: Set<string>,
): boolean {
  return !element.layout.parentId || !pastedSourceIds.has(element.layout.parentId);
}
